import { useState } from 'react';
import { usePTS } from '../hooks/usePTS';
import { usePatients } from '../hooks/usePatients';
import { useProfessionals } from '../hooks/useProfessionals';
import { PTS as PTSType } from '../types';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Users,
  Target,
  CheckCircle,
  Calendar
} from 'lucide-react';
import PTSForm from '../components/PTSForm';
import PTSDetails from '../components/PTSDetails';

export default function PTS() {
  const { ptsList, loading, deletePTS } = usePTS();
  const { patients } = usePatients();
  const { professionals } = useProfessionals();
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedPTS, setSelectedPTS] = useState<PTSType | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredPTS = ptsList.filter(pts => {
    const patient = patients.find(p => p.id === pts.patientId);
    const coordinator = professionals.find(p => p.id === pts.coordinatorId);
    
    const matchesSearch = patient?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         coordinator?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || pts.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleEdit = (pts: PTSType) => {
    setSelectedPTS(pts);
    setShowForm(true);
  };

  const handleDelete = async (pts: PTSType) => {
    if (window.confirm('Tem certeza que deseja excluir este PTS?')) {
      await deletePTS(pts.id);
    }
  };

  const handleViewDetails = (pts: PTSType) => {
    setSelectedPTS(pts);
    setShowDetails(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ativo':
        return 'bg-green-100 text-green-800';
      case 'concluido':
        return 'bg-blue-100 text-blue-800';
      case 'suspenso':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusName = (status: string) => {
    const statusNames = {
      'ativo': 'Ativo',
      'concluido': 'Concluído',
      'suspenso': 'Suspenso'
    };
    return statusNames[status as keyof typeof statusNames] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projeto Terapêutico Singular (PTS)</h1>
          <p className="text-gray-600">Gerencie os projetos terapêuticos dos pacientes</p>
        </div>
        <button
          onClick={() => {
            setSelectedPTS(null);
            setShowForm(true);
          }}
          className="bg-medical-600 text-white px-4 py-2 rounded-lg hover:bg-medical-700 flex items-center space-x-2 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Novo PTS</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Buscar por paciente ou coordenador..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
          >
            <option value="all">Todos os Status</option>
            <option value="ativo">Ativo</option>
            <option value="concluido">Concluído</option>
            <option value="suspenso">Suspenso</option>
          </select>
        </div>
      </div>

      {/* PTS Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPTS.map((pts) => {
          const patient = patients.find(p => p.id === pts.patientId);
          const coordinator = professionals.find(p => p.id === pts.coordinatorId);
          const completedActions = pts.actions.filter(action => action.status === 'concluido').length;
          const totalActions = pts.actions.length;
          
          return (
            <div key={pts.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-medical-100 rounded-full flex items-center justify-center">
                      <Target className="h-6 w-6 text-medical-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {patient?.name || 'Paciente não encontrado'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Coord: {coordinator?.name || 'Não encontrado'}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(pts.status)}`}>
                    {getStatusName(pts.status)}
                  </span>
                </div>

                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-center space-x-2">
                    <Target className="h-4 w-4" />
                    <span>{pts.objectives.length} objetivo(s)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4" />
                    <span>{completedActions}/{totalActions} ações concluídas</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4" />
                    <span>{pts.team.length} profissional(is) na equipe</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Criado em {new Date(pts.createdAt).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>Progresso</span>
                    <span>{totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-medical-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${totalActions > 0 ? (completedActions / totalActions) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      {pts.evaluations.length} avaliação(ões)
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(pts)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(pts)}
                        className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(pts)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredPTS.length === 0 && (
        <div className="text-center py-12">
          <Target className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum PTS encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Tente ajustar os termos de busca.' : 'Comece criando um novo PTS.'}
          </p>
        </div>
      )}

      {/* PTS Form Modal */}
      {showForm && (
        <PTSForm
          pts={selectedPTS}
          onClose={() => {
            setShowForm(false);
            setSelectedPTS(null);
          }}
        />
      )}

      {/* PTS Details Modal */}
      {showDetails && selectedPTS && (
        <PTSDetails
          pts={selectedPTS}
          onClose={() => {
            setShowDetails(false);
            setSelectedPTS(null);
          }}
        />
      )}
    </div>
  );
}